/**
 * =========================================================================
 * index.tsx – Einstiegspunkt der Xeno-Admin-App
 * =========================================================================
 *
 * Bootstrapping: Rendert die React-Root-Komponente in #root (index.html).
 *
 * Ablauf:
 *   1. Alte Hash-URLs (#/pfad) auf saubere URLs (/pfad) umleiten.
 *   2. App.tsx lazy laden (mit Retry bei Netzwerkfehler).
 *   3. AuthProvider umschließt die App (Login/Session).
 *   4. ErrorBoundary fängt unkontrollierte Render-Fehler ab.
 *
 * Zusammenhänge:
 *   - App.tsx        → Hauptkomponente (Layout, Routing, Sidebar, TopNav)
 *   - AuthContext    → Login-Status, Session-Prüfung
 *   - index.html     → HTML-Shell mit #root, CSS-Variablen (Typografie)
 *   - tailwind.css   → Tailwind-Direktiven (PostCSS-Build, kein CDN)
 *   - app.css        → System-CSS (Typografie, Toolbar, Scrollbar) – NACH Tailwind importiert
 *   - eventplanner.css, inter.css → globale CSS-Importe (Vite findet sie nur per Import)
 * =========================================================================
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from '@/context/AuthContext';
import '@/styles/tailwind.css';
import '@/styles/app.css';
import '@/components/EventPlanner/eventplanner.css';
/** Inter-Font: per Import damit Vite die Datei beim Build findet (index.html-Link liegt außerhalb von public). */
import '../public/vendor/fonts/inter.css';

/**
 * Redirect für alte Hash-URLs (#/persons/123 → /basepath/persons/123).
 * Alte Bookmarks oder externe Links mit #-Fragment werden einmalig
 * auf die saubere Browser-URL umgeleitet. Der Basispfad (Unterverzeichnis)
 * wird beibehalten.
 */
if (window.location.hash && window.location.hash.startsWith('#/')) {
  const route = window.location.hash.slice(1);
  const base = window.location.pathname.replace(/\/index\.html$/, '').replace(/\/+$/, '');
  window.history.replaceState(null, '', base + route);
}

/**
 * Globale Fehlergrenzen-Komponente.
 * Fängt unkontrollierte Render-Fehler ab und zeigt eine Fehlerseite mit Retry-Button.
 * Wird in Root() um die gesamte App gelegt.
 */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; onRetry?: () => void },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div
          style={{
            padding: 32,
            fontFamily: 'sans-serif',
            background: '#fef2f2',
            minHeight: '100vh',
            maxWidth: 640,
            margin: '0 auto',
          }}
        >
          <h1 style={{ color: '#b91c1c', fontSize: '1.5rem', marginBottom: 16 }}>
            Fehler beim Rendern
          </h1>
          <pre
            style={{
              background: '#fff',
              padding: 20,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: '1rem',
              lineHeight: 1.5,
              border: '1px solid #fecaca',
              borderRadius: 8,
              marginBottom: 24,
            }}
          >
            {this.state.error.message}
          </pre>
          <p style={{ color: '#666', marginBottom: 24 }}>
            Browser-Konsole (F12) für Stack-Trace öffnen.
          </p>
          {this.props.onRetry && (
            <button
              type="button"
              onClick={() => {
                this.setState({ hasError: false, error: null });
                this.props.onRetry?.();
              }}
              style={{
                padding: '12px 24px',
                fontSize: '1rem',
                background: '#b91c1c',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Erneut versuchen
            </button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

/** Maximale Anzahl Lade-Versuche für App.tsx (bei Netzwerkfehler). */
const MAX_LOAD_RETRIES = 2;
/** Wartezeit zwischen Lade-Versuchen (ms). */
const RETRY_DELAY_MS = 800;

/**
 * Lädt App.tsx per dynamischem Import (Code-Splitting).
 * Wird von Root() mit Retry-Logik aufgerufen.
 */
function loadApp(): Promise<{ default: React.ComponentType }> {
  return import('./App').then((m) => ({ default: m.default }));
}

/**
 * Root-Komponente: Lädt App.tsx lazy (mit Retry), zeigt Lade-/Fehler-Screen
 * und umschließt die App mit ErrorBoundary + AuthProvider.
 */
function Root() {
  const [App, setApp] = useState<React.ComponentType | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const tryLoadApp = React.useCallback(() => {
    setLoadError(null);
    let attempt = 0;
    const attemptLoad = (): Promise<void> =>
      loadApp()
        .then((m) => setApp(() => m.default))
        .catch((err: Error) => {
          attempt += 1;
          if (attempt <= MAX_LOAD_RETRIES) {
            return new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS)).then(attemptLoad);
          }
          setLoadError(err.message || String(err));
        });
    attemptLoad();
  }, []);

  useEffect(() => {
    tryLoadApp();
  }, [tryLoadApp]);

  if (loadError) {
    return (
      <div
        style={{
          padding: 32,
          fontFamily: 'sans-serif',
          background: '#fef2f2',
          minHeight: '100vh',
          maxWidth: 640,
          margin: '0 auto',
        }}
      >
        <h1 style={{ color: '#b91c1c', fontSize: '1.5rem', marginBottom: 16 }}>
          Fehler beim Laden der App
        </h1>
        <pre
          style={{
            background: '#fff',
            padding: 20,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            fontSize: '1rem',
            lineHeight: 1.5,
            border: '1px solid #fecaca',
            borderRadius: 8,
            marginBottom: 24,
          }}
        >
          {loadError}
        </pre>
        <p style={{ color: '#666', marginBottom: 24 }}>Browser-Konsole (F12) öffnen für Details.</p>
        <button
          type="button"
          onClick={tryLoadApp}
          style={{
            padding: '12px 24px',
            fontSize: '1rem',
            background: '#b91c1c',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!App) {
    return (
      <div style={{ padding: 24, fontFamily: 'sans-serif', background: '#f8fafc', minHeight: '100vh' }}>
        <p style={{ color: '#64748b' }}>Wird geladen…</p>
      </div>
    );
  }

  return (
    <ErrorBoundary onRetry={() => window.location.reload()}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ErrorBoundary>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

// Service Worker fuer Web Push Notifications registrieren.
// sw.js liegt im public/-Ordner und wird von Vite nach dist/ kopiert.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('./sw.js').catch(() => {
    // Registrierung fehlgeschlagen (z.B. kein HTTPS) – kein User-Feedback noetig
  });
}
